import React from "react";
import {
    faCashRegister,
    faUsers,
    faCheckCircle,
    faTimesCircle,
    faMoneyBillWave,
    faMale,
    faFemale
} from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Spinner } from '@themesberg/react-bootstrap';
import {
    BankStatsWidget,
    BarChartWidget,
    CircleChartWidget,
    CounterWidget,
    ProgressRepWidget,
    ProgressTrackWidget,
    LineChartWidget,
} from "../../components/Widgets";
import { connect } from "react-redux";
import { getDashboardData } from "../../actions/app";
import { numberWithCommas } from "../../helpers/dateHelper";

class DashboardOverview extends React.Component {
    state = {
        loading: false,
    }

    componentDidMount = async () => {
        const { getDashboardData, dates, } = this.props;
        if (dates) {
            await getDashboardData(dates);
            this.setState({ hasFetched: true });
        }
    }

    componentDidUpdate = async (prevProps, prevState, snapshot) => {
        const { dates, getDashboardData, error } = this.props;

        // Only fetch if dates changed and we haven't errored out
        const datesChanged = prevProps.dates !== dates;
        if (dates && datesChanged && !error) {
            await getDashboardData(dates);
            this.setState({ hasFetched: true });
        }
    }

    handleRetry = async () => {
        const { getDashboardData, dates } = this.props;
        this.setState({ hasFetched: false });
        if (dates) {
            await getDashboardData(dates);
            this.setState({ hasFetched: true });
        }
    }

    render() {
        const { dashboard, user, loadingState, error } = this.props;
        const isPartnerUser = (user?.userPartnerType ?? "").toLowerCase() !== 'i';

        if (loadingState) {
            return (
                <Row className={'mt-5'}>
                    <Col xs={12} className="d-flex justify-content-center align-items-center">
                        <Spinner animation="border" role="status" className="me-2" />
                        <span>Loading dashboard...</span>
                    </Col>
                </Row>
            );
        }

        // Display error state with retry option
        if (error && !dashboard) {
            return (
                <Row className={'mt-5'}>
                    <Col xs={12} className="d-flex flex-column justify-content-center align-items-center">
                        <div className="text-center">
                            <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
                            <h4 className="text-danger mb-3">Failed to Load Dashboard</h4>
                            <p className="text-muted mb-4">{error}</p>
                            <button
                                className="btn btn-primary"
                                onClick={this.handleRetry}
                            >
                                <i className="fas fa-redo me-2"></i>
                                Retry
                            </button>
                        </div>
                    </Col>
                </Row>
            );
        }

        // Custom compact card style
        const compactCardStyle = {
            transform: 'scale(0.9)',
            transformOrigin: 'top left',
            marginBottom: '-10px'
        };

        if (isPartnerUser) {
            const approved = dashboard?.loanStatusCounts?.Approved ?? 0;
            const closed = dashboard?.loanStatusCounts?.Closed ?? 0;
            const periodicTransactions = dashboard?.periodicTotalTransactions ?? 0;
            const approvedLoansByDistrict = dashboard?.approvedLoansByDistrict ?? {};
            const totalApproved = Object.values(approvedLoansByDistrict).reduce((sum, v) => sum + (v ?? 0), 0);
            const customerGenderBreakdown = dashboard?.preCustomerGenderBreakdown ?? {};
            const maleCount = customerGenderBreakdown['Male'] ?? 0;
            const femaleCount = customerGenderBreakdown['Female'] ?? 0;

            const generateDistinctColors = (n) => {
                if (n <= 0) return [];
                const palette = [
                    'hsl(210, 70%, 50%)', 'hsl(25, 85%, 55%)', 'hsl(140, 60%, 45%)',
                    'hsl(330, 70%, 55%)', 'hsl(45, 90%, 55%)', 'hsl(280, 65%, 55%)',
                ];
                if (n <= palette.length) return palette.slice(0, n);
                const colors = [];
                for (let i = 0; i < n; i++) {
                    colors.push(`hsl(${Math.round((360 / n) * i)}, 75%, 50%)`);
                }
                return colors;
            };

            const districtEntries = Object.entries(approvedLoansByDistrict);
            const districtColors = generateDistinctColors(districtEntries.length);
            const districtData = districtEntries.map(([district, count], idx) => ({
                id: idx,
                label: district,
                color: districtColors[idx],
                value: count ?? 0,
            }));

            return (
                <>
                    {/* Primary KPIs Row - Compact 3 cards */}
                    <Row className="mt-3 mb-3">
                        <Col xs={12} md={4} className="mb-3" style={compactCardStyle}>
                            <CounterWidget
                                category="Approved Loans"
                                title={numberWithCommas(`${approved}`)}
                                period=""
                                percentage={numberWithCommas(`${approved}`)}
                                icon={faCheckCircle}
                                iconColor="shape-secondary"
                            />
                        </Col>

                        <Col xs={12} md={4} className="mb-3" style={compactCardStyle}>
                            <CounterWidget
                                category="Closed Loans"
                                title={numberWithCommas(`${closed}`)}
                                period=""
                                percentage={numberWithCommas(`${closed}`)}
                                icon={faTimesCircle}
                                iconColor="shape-primary"
                            />
                        </Col>

                        <Col xs={12} md={4} className="mb-3" style={compactCardStyle}>
                            <CounterWidget
                                category="Total Transactions"
                                title={numberWithCommas(`${periodicTransactions}`)}
                                period=""
                                percentage={numberWithCommas(`${periodicTransactions}`)}
                                icon={faMoneyBillWave}
                                iconColor="shape-tertiary"
                            />
                        </Col>
                    </Row>

                    {/* Gender Breakdown Row */}
                    <Row className="mb-3">
                        <Col xs={12} md={6} className="mb-3" style={compactCardStyle}>
                            <CounterWidget
                                category="Male Customers"
                                title={numberWithCommas(`${maleCount}`)}
                                period=""
                                percentage={numberWithCommas(`${maleCount}`)}
                                icon={faMale}
                                iconColor="shape-secondary"
                            />
                        </Col>

                        <Col xs={12} md={6} className="mb-3" style={compactCardStyle}>
                            <CounterWidget
                                category="Female Customers"
                                title={numberWithCommas(`${femaleCount}`)}
                                period=""
                                percentage={numberWithCommas(`${femaleCount}`)}
                                icon={faFemale}
                                iconColor="shape-primary"
                            />
                        </Col>
                    </Row>

                    {/* District Breakdown Chart */}
                    {districtData.length >= 1 && (
                        <Row className="mb-3">
                            <Col xs={12}>
                                <BarChartWidget
                                    title="Approved Loans by District"
                                    value={numberWithCommas(`${totalApproved}`)}
                                    percentage={0}
                                    data={districtData}
                                />
                            </Col>
                        </Row>
                    )}

                    {/* New Charts - Loan Status Overview and Device Categories */}
                    <Row className="mb-3">
                        <Col xs={12} lg={6} className="mb-3">
                            <CircleChartWidget
                                title="Loan Status Distribution"
                                data={Object.entries(dashboard?.allLoanStatusCounts ?? {}).map(([label, value]) => ({
                                    label,
                                    value
                                }))}
                            />
                        </Col>

                        <Col xs={12} lg={6} className="mb-3">
                            <CircleChartWidget
                                title="Device Categories"
                                data={Object.entries(dashboard?.partnerDeviceCategories ?? {}).map(([label, value]) => ({
                                    label,
                                    value
                                }))}
                            />
                        </Col>
                    </Row>

                    {/* Monthly Loan Approval Trend */}
                    {Object.keys(dashboard?.monthlyLoanTrend ?? {}).length > 0 && (
                        <Row className="mb-3">
                            <Col xs={12}>
                                <LineChartWidget
                                    title="Monthly Loan Approval Trend"
                                    data={dashboard?.monthlyLoanTrend ?? {}}
                                />
                            </Col>
                        </Row>
                    )}
                </>
            );
        }

        // Non-partner user dashboard - Compact cards
        return (
            <>
                {/* Primary KPIs Row - 3 compact cards */}
                <Row className="mt-3 mb-3">
                    <Col xs={12} md={4} className="mb-3" style={compactCardStyle}>
                        <CounterWidget
                            category="Active Customers"
                            title={numberWithCommas((`${dashboard?.activeCustomers ?? ''}`) ?? 0)}
                            period=""
                            percentage={numberWithCommas(`${dashboard?.activeCustomers ?? ''}` ?? 0)}
                            icon={faUsers}
                            iconColor="shape-secondary"
                        />
                    </Col>

                    <Col xs={12} md={4} className="mb-3" style={compactCardStyle}>
                        <CounterWidget
                            category="Turnover"
                            title={numberWithCommas((`${dashboard?.turnover ?? ""}`) ?? 0)}
                            period=""
                            percentage={numberWithCommas((`${dashboard?.turnover ?? ""}`.toLocaleString()))}
                            icon={faMoneyBillWave}
                            iconColor="shape-tertiary"
                        />
                    </Col>

                    <Col xs={12} md={4} className="mb-3" style={compactCardStyle}>
                        <CounterWidget
                            category="Total Transactions"
                            title={numberWithCommas(`${dashboard?.periodicTotalTransactions ?? 0}`)}
                            period=""
                            percentage={numberWithCommas(`${dashboard?.periodicTotalTransactions ?? 0}`)}
                            icon={faCashRegister}
                            iconColor="shape-primary"
                        />
                    </Col>
                </Row>

                {/* Charts and Widgets Row */}
                <Row className="mb-3">
                    <Col xs={12} lg={6} className="mb-3">
                        <CircleChartWidget
                            title="Top Device Categories"
                            data={dashboard?.deviceCategories ?? []}
                        />
                    </Col>

                    <Col xs={12} lg={6} className="mb-3">
                        <BankStatsWidget banks={dashboard?.banks ?? []} />
                    </Col>
                </Row>

                {/* Stores and Representatives Row */}
                <Row className="mb-3">
                    <Col xs={12} lg={6} className="mb-3">
                        <ProgressTrackWidget stores={dashboard?.stores ?? []} />
                    </Col>

                    <Col xs={12} lg={6} className="mb-3">
                        <ProgressRepWidget reps={dashboard?.dashreps ?? []} />
                    </Col>
                </Row>

                {/* New Charts - Loan Status and Gender Distribution */}
                <Row className="mb-3">
                    <Col xs={12} lg={6} className="mb-3">
                        <CircleChartWidget
                            title="Loan Status Distribution"
                            data={Object.entries(dashboard?.loanStatusDistribution ?? {}).map(([label, value]) => ({
                                label,
                                value
                            }))}
                        />
                    </Col>

                    <Col xs={12} lg={6} className="mb-3">
                        <CircleChartWidget
                            title="Gender Distribution"
                            data={Object.entries(dashboard?.genderDistribution ?? {}).map(([label, value]) => ({
                                label,
                                value
                            }))}
                        />
                    </Col>
                </Row>

                {/* Monthly Trends */}
                {Object.keys(dashboard?.monthlyLoanTrend ?? {}).length > 0 && (
                    <Row className="mb-3">
                        <Col xs={12} lg={6} className="mb-3">
                            <LineChartWidget
                                title="Monthly Loan Approval Trend"
                                data={dashboard?.monthlyLoanTrend ?? {}}
                            />
                        </Col>

                        <Col xs={12} lg={6} className="mb-3">
                            <LineChartWidget
                                title="Monthly Turnover Trend"
                                data={dashboard?.monthlyTurnoverTrend ?? {}}
                            />
                        </Col>
                    </Row>
                )}
            </>
        );
    }
}

const mapStateProps = (store) => {
    const { dashboard, error, dates, loadingState } = store.app;
    const { user } = store.profile;

    return { dashboard, error, dates, user, loadingState };
}

export default connect(mapStateProps, {
    getDashboardData,
})(DashboardOverview);
